import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { findingsAPI, deficiencyAPI } from '../lib/api';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  MinusCircle,
  HelpCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle,
  Loader2,
  Download,
  Wrench,
  Shield,
  BarChart3,
  Upload,
  Trash2,
  Save,
  CheckCircle2,
  Clock,
  AlertCircle,
  Filter,
  ArrowUpDown,
  Flame,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Findings = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [activeTab, setActiveTab] = useState('summary');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Filtering and sorting state
  const [priorityFilter, setPriorityFilter] = useState('all'); // all, High, Medium, Low
  const [sortBy, setSortBy] = useState('priority'); // priority, category, status
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  
  // Remediation state
  const [editingRemediation, setEditingRemediation] = useState({});
  const [savingRemediation, setSavingRemediation] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState({});
  const [savingPriority, setSavingPriority] = useState({});

  useEffect(() => {
    fetchFindings();
  }, [id]);

  const fetchFindings = async () => {
    try {
      const response = await findingsAPI.getFindings(id);
      setData(response.data);
      
      // Initialize editing state for deficiencies
      const editState = {};
      response.data?.findings?.nonCompliant?.forEach(f => {
        editState[f.questionId] = {
          remediationComment: f.remediationComment || '',
          remediationStatus: f.remediationStatus || 'Open',
          remediationDocuments: f.remediationDocuments || [],
          priority: f.priority || 'Medium',
          priorityNotes: f.priorityNotes || ''
        };
      });
      setEditingRemediation(editState);
    } catch (error) {
      console.error('Failed to fetch findings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemediationChange = (questionId, field, value) => {
    setEditingRemediation(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  const savePriority = async (questionId) => {
    setSavingPriority(prev => ({ ...prev, [questionId]: true }));
    try {
      const remData = editingRemediation[questionId];
      await deficiencyAPI.updatePriority(id, questionId, {
        priority: remData.priority,
        notes: remData.priorityNotes
      });
      await fetchFindings();
    } catch (error) {
      console.error('Failed to save priority:', error);
    } finally {
      setSavingPriority(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const saveRemediation = async (questionId) => {
    setSavingRemediation(prev => ({ ...prev, [questionId]: true }));
    try {
      const remData = editingRemediation[questionId];
      await deficiencyAPI.updateRemediation(id, questionId, {
        questionId,
        remediationComment: remData.remediationComment,
        remediationStatus: remData.remediationStatus,
        remediationDocuments: remData.remediationDocuments.map(d => d.id || d)
      });
      
      // Also save priority
      await deficiencyAPI.updatePriority(id, questionId, {
        priority: remData.priority,
        notes: remData.priorityNotes
      });
      
      await fetchFindings();
    } catch (error) {
      console.error('Failed to save remediation:', error);
    } finally {
      setSavingRemediation(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleFileUpload = async (questionId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingDoc(prev => ({ ...prev, [questionId]: true }));
    try {
      await deficiencyAPI.uploadDocument(id, questionId, file);
      await fetchFindings();
    } catch (error) {
      console.error('Failed to upload document:', error);
    } finally {
      setUploadingDoc(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleDeleteDocument = async (questionId, documentId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await deficiencyAPI.deleteDocument(id, questionId, documentId);
      await fetchFindings();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  const toggleExpand = (questionId) => {
    setExpandedItems(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // Filter and sort deficiencies
  const getFilteredDeficiencies = () => {
    if (!data?.findings?.nonCompliant) return [];
    
    let filtered = [...data.findings.nonCompliant];
    
    // Apply priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(d => d.priority === priorityFilter);
    }
    
    // Apply sorting
    const priorityOrder = { 'High': 0, 'Medium': 1, 'Low': 2 };
    const statusOrder = { 'Open': 0, 'In Progress': 1, 'Remediated': 2 };
    
    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'priority') {
        comparison = priorityOrder[a.priority || 'Medium'] - priorityOrder[b.priority || 'Medium'];
      } else if (sortBy === 'category') {
        comparison = (a.category || '').localeCompare(b.category || '');
      } else if (sortBy === 'status') {
        comparison = statusOrder[a.remediationStatus || 'Open'] - statusOrder[b.remediationStatus || 'Open'];
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  const generatePDF = async () => {
    if (!data) return;
    setGeneratingPdf(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFillColor(109, 40, 217);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.text('Assessment Findings Report', 20, 20);
      doc.setFontSize(14);
      doc.text(data.assessmentName, 20, 32);
      doc.setFontSize(10);
      doc.text(`${data.assessmentType} Assessment | Score: ${data.score}%`, 20, 40);
      
      // Summary section
      let yPos = 60;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.text('Executive Summary', 20, yPos);
      
      yPos += 15;
      doc.setFontSize(11);
      doc.text(`Total Questions: ${data.summary.totalQuestions}`, 20, yPos);
      yPos += 7;
      doc.text(`Compliant (Yes): ${data.summary.compliantCount}`, 20, yPos);
      yPos += 7;
      doc.text(`Non-Compliant (No): ${data.summary.nonCompliantCount}`, 20, yPos);
      yPos += 7;
      doc.text(`Not Applicable: ${data.summary.notApplicableCount}`, 20, yPos);
      yPos += 7;
      doc.text(`Unanswered: ${data.summary.unansweredCount}`, 20, yPos);
      
      // Priority breakdown
      if (data.summary.prioritySummary) {
        yPos += 15;
        doc.setFontSize(14);
        doc.text('Deficiency Priority Breakdown', 20, yPos);
        yPos += 10;
        doc.setFontSize(11);
        doc.setTextColor(220, 38, 38);
        doc.text(`High Priority: ${data.summary.prioritySummary.high}`, 20, yPos);
        yPos += 7;
        doc.setTextColor(234, 179, 8);
        doc.text(`Medium Priority: ${data.summary.prioritySummary.medium}`, 20, yPos);
        yPos += 7;
        doc.setTextColor(34, 197, 94);
        doc.text(`Low Priority: ${data.summary.prioritySummary.low}`, 20, yPos);
      }
      
      // Non-compliant items with remediation
      if (data.findings.nonCompliant.length > 0) {
        doc.addPage();
        doc.setFontSize(16);
        doc.setTextColor(220, 38, 38);
        doc.text('Non-Compliant Findings & Remediation', 20, 20);
        
        const nonCompliantData = data.findings.nonCompliant.map((f, i) => [
          i + 1,
          f.questionId,
          f.priority || 'Medium',
          f.category,
          (f.questionText || '').substring(0, 50) + '...',
          f.remediationStatus || 'Open'
        ]);
        
        autoTable(doc, {
          startY: 30,
          head: [['#', 'ID', 'Priority', 'Category', 'Finding', 'Status']],
          body: nonCompliantData,
          theme: 'striped',
          headStyles: { fillColor: [220, 38, 38] },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 25 },
            2: { cellWidth: 20 },
            3: { cellWidth: 25 },
            4: { cellWidth: 55 },
            5: { cellWidth: 25 }
          },
          styles: { fontSize: 8 },
          didParseCell: function(data) {
            if (data.column.index === 2 && data.section === 'body') {
              const priority = data.cell.text[0];
              if (priority === 'High') {
                data.cell.styles.textColor = [220, 38, 38];
                data.cell.styles.fontStyle = 'bold';
              } else if (priority === 'Medium') {
                data.cell.styles.textColor = [234, 179, 8];
              } else {
                data.cell.styles.textColor = [34, 197, 94];
              }
            }
            if (data.column.index === 5 && data.section === 'body') {
              const status = data.cell.text[0];
              if (status === 'Remediated') {
                data.cell.styles.textColor = [34, 197, 94];
              } else if (status === 'In Progress') {
                data.cell.styles.textColor = [234, 179, 8];
              } else {
                data.cell.styles.textColor = [239, 68, 68];
              }
            }
          }
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Aspiritech Cybersecurity Assessment - Generated ${new Date().toLocaleString()} - Page ${i} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
      
      doc.save(`${data.assessmentName}-findings.pdf`);
    } catch (error) {
      console.error('PDF generation failed:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'High':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full font-medium">
            <Flame className="w-3 h-3" />
            High
          </span>
        );
      case 'Low':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full font-medium">
            <TrendingDown className="w-3 h-3" />
            Low
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full font-medium">
            <TrendingUp className="w-3 h-3" />
            Medium
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <p className="text-white">Failed to load findings</p>
      </div>
    );
  }

  const renderFindingCard = (finding, type) => {
    const isExpanded = expandedItems[finding.questionId];
    const attachments = data.attachmentsByQuestion?.[finding.questionId] || [];
    const remData = editingRemediation[finding.questionId] || {};
    const isSaving = savingRemediation[finding.questionId];
    const isUploading = uploadingDoc[finding.questionId];
    const isSavingPriority = savingPriority[finding.questionId];
    
    const getStatusBadge = (status) => {
      switch (status) {
        case 'Remediated':
          return (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
              <CheckCircle2 className="w-3 h-3" />
              Remediated
            </span>
          );
        case 'In Progress':
          return (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
              <Clock className="w-3 h-3" />
              In Progress
            </span>
          );
        default:
          return (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
              <AlertCircle className="w-3 h-3" />
              Open
            </span>
          );
      }
    };
    
    return (
      <div 
        key={finding.questionId}
        data-testid={`finding-card-${finding.questionId}`}
        className={`bg-slate-800/50 rounded-xl border ${
          type === 'compliant' ? 'border-green-500/30' :
          type === 'nonCompliant' ? (finding.remediationStatus === 'Remediated' ? 'border-green-500/30' : 
            finding.priority === 'High' ? 'border-red-500/50' : 'border-red-500/30') :
          type === 'notApplicable' ? 'border-slate-600' :
          'border-yellow-500/30'
        } overflow-hidden`}
      >
        <button
          onClick={() => toggleExpand(finding.questionId)}
          className="w-full p-4 text-left flex items-start gap-4"
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            type === 'compliant' ? 'bg-green-500/20' :
            type === 'nonCompliant' ? (finding.remediationStatus === 'Remediated' ? 'bg-green-500/20' : 
              finding.priority === 'High' ? 'bg-red-600/30' : 'bg-red-500/20') :
            type === 'notApplicable' ? 'bg-slate-700' :
            'bg-yellow-500/20'
          }`}>
            {type === 'compliant' && <CheckCircle className="w-5 h-5 text-green-400" />}
            {type === 'nonCompliant' && (finding.remediationStatus === 'Remediated' 
              ? <CheckCircle2 className="w-5 h-5 text-green-400" />
              : finding.priority === 'High' 
              ? <Flame className="w-5 h-5 text-red-400" />
              : <XCircle className="w-5 h-5 text-red-400" />
            )}
            {type === 'notApplicable' && <MinusCircle className="w-5 h-5 text-slate-400" />}
            {type === 'unanswered' && <HelpCircle className="w-5 h-5 text-yellow-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-mono text-slate-500">{finding.questionId}</span>
              <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded">
                {finding.category}
              </span>
              {type === 'nonCompliant' && getPriorityBadge(finding.priority || 'Medium')}
              {type === 'nonCompliant' && getStatusBadge(finding.remediationStatus || 'Open')}
            </div>
            <p className="text-white text-sm line-clamp-2">{finding.questionText}</p>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
          )}
        </button>
        
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-slate-700 pt-4">
            {/* Reference */}
            <div className="flex items-start gap-3">
              <FileText className="w-4 h-4 text-slate-500 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500 mb-1">Reference</p>
                <p className="text-sm text-slate-300">{finding.referenceText}</p>
                {finding.nistUrl && (
                  <a
                    href={finding.nistUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 mt-2"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View NIST/HHS Guidance
                  </a>
                )}
              </div>
            </div>

            {/* Suggested Remediation Steps (for non-compliant) */}
            {type === 'nonCompliant' && finding.remediation && (
              <div className="flex items-start gap-3">
                <Wrench className="w-4 h-4 text-violet-400 mt-0.5" />
                <div>
                  <p className="text-xs text-violet-400 mb-2 font-medium">Suggested Remediation Steps</p>
                  <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg p-3">
                    {finding.remediation.split('\n').map((step, i) => (
                      <p key={i} className="text-sm text-slate-300 mb-1">{step}</p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Original Comment from Assessment */}
            {finding.comment && (
              <div className="flex items-start gap-3">
                <FileText className="w-4 h-4 text-slate-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500 mb-1">Assessment Comment</p>
                  <p className="text-sm text-slate-300">{finding.comment}</p>
                </div>
              </div>
            )}

            {/* Attachments */}
            {attachments.length > 0 && (
              <div className="flex items-start gap-3">
                <FileText className="w-4 h-4 text-slate-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500 mb-2">Assessment Attachments ({attachments.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((att) => (
                      <span key={att.id} className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">
                        {att.fileName}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Priority & Remediation Section (only for non-compliant) */}
            {type === 'nonCompliant' && (
              <div className="mt-4 pt-4 border-t border-slate-600">
                <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-violet-400" />
                  Priority & Remediation Tracking
                </h4>
                
                {/* Priority Selection */}
                <div className="mb-4">
                  <label className="block text-xs text-slate-400 mb-2">Priority Level</label>
                  <div className="flex gap-2">
                    {['High', 'Medium', 'Low'].map((priority) => (
                      <button
                        key={priority}
                        onClick={() => handleRemediationChange(finding.questionId, 'priority', priority)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                          remData.priority === priority
                            ? priority === 'High' 
                              ? 'bg-red-600 text-white'
                              : priority === 'Medium'
                              ? 'bg-yellow-600 text-white'
                              : 'bg-green-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                        data-testid={`priority-btn-${priority.toLowerCase()}`}
                      >
                        {priority === 'High' && <Flame className="w-3 h-3" />}
                        {priority === 'Medium' && <TrendingUp className="w-3 h-3" />}
                        {priority === 'Low' && <TrendingDown className="w-3 h-3" />}
                        {priority}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Remediation Status */}
                <div className="mb-4">
                  <label className="block text-xs text-slate-400 mb-2">Remediation Status</label>
                  <div className="flex gap-2">
                    {['Open', 'In Progress', 'Remediated'].map((status) => (
                      <button
                        key={status}
                        onClick={() => handleRemediationChange(finding.questionId, 'remediationStatus', status)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          remData.remediationStatus === status
                            ? status === 'Remediated' 
                              ? 'bg-green-600 text-white'
                              : status === 'In Progress'
                              ? 'bg-yellow-600 text-white'
                              : 'bg-red-600 text-white'
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {status === 'Remediated' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                        {status === 'In Progress' && <Clock className="w-3 h-3 inline mr-1" />}
                        {status === 'Open' && <AlertCircle className="w-3 h-3 inline mr-1" />}
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Remediation Comment */}
                <div className="mb-4">
                  <label className="block text-xs text-slate-400 mb-2">Remediation Notes</label>
                  <textarea
                    value={remData.remediationComment || ''}
                    onChange={(e) => handleRemediationChange(finding.questionId, 'remediationComment', e.target.value)}
                    placeholder="Document remediation steps taken, timeline, responsible parties..."
                    rows={3}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
                  />
                </div>

                {/* Remediation Documents */}
                <div className="mb-4">
                  <label className="block text-xs text-slate-400 mb-2">Evidence Documents</label>
                  
                  {remData.remediationDocuments?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {remData.remediationDocuments.map((doc) => (
                        <div key={doc.id} className="flex items-center gap-1 px-2 py-1 bg-slate-700 rounded text-sm">
                          <FileText className="w-3 h-3 text-slate-400" />
                          <span className="text-slate-300">{doc.fileName}</span>
                          <button
                            onClick={() => handleDeleteDocument(finding.questionId, doc.id)}
                            className="ml-1 p-0.5 text-slate-400 hover:text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg cursor-pointer transition-colors text-sm">
                    {isUploading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    Upload Document
                    <input
                      type="file"
                      onChange={(e) => handleFileUpload(finding.questionId, e)}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                </div>

                {/* Save Button */}
                <button
                  onClick={() => saveRemediation(finding.questionId)}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
                  data-testid="save-remediation-btn"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Changes
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredDeficiencies = getFilteredDeficiencies();

  return (
    <div data-testid="findings-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/assessment/${id}`)}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">Assessment Findings</h1>
                <p className="text-slate-400 text-sm">{data.assessmentName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/report/${id}`)}
                className="flex items-center gap-2 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Charts
              </button>
              <button
                onClick={generatePDF}
                disabled={generatingPdf}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {generatingPdf ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Export PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="text-3xl font-bold text-white">{data.score}%</div>
            <div className="text-sm text-slate-400">Overall Score</div>
          </div>
          <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
            <div className="text-3xl font-bold text-green-400">{data.summary.compliantCount}</div>
            <div className="text-sm text-green-300">Compliant</div>
          </div>
          <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
            <div className="text-3xl font-bold text-red-400">{data.summary.nonCompliantCount}</div>
            <div className="text-sm text-red-300">Non-Compliant</div>
          </div>
          <div className="bg-slate-700/50 rounded-xl p-4 border border-slate-600">
            <div className="text-3xl font-bold text-slate-300">{data.summary.notApplicableCount}</div>
            <div className="text-sm text-slate-400">N/A</div>
          </div>
          <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
            <div className="text-3xl font-bold text-yellow-400">{data.summary.unansweredCount}</div>
            <div className="text-sm text-yellow-300">Unanswered</div>
          </div>
        </div>

        {/* Priority Summary */}
        {data.summary.prioritySummary && data.summary.nonCompliantCount > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30 flex items-center gap-3">
              <Flame className="w-8 h-8 text-red-400" />
              <div>
                <div className="text-2xl font-bold text-red-400">{data.summary.prioritySummary.high}</div>
                <div className="text-sm text-red-300">High Priority</div>
              </div>
            </div>
            <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-yellow-400" />
              <div>
                <div className="text-2xl font-bold text-yellow-400">{data.summary.prioritySummary.medium}</div>
                <div className="text-sm text-yellow-300">Medium Priority</div>
              </div>
            </div>
            <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30 flex items-center gap-3">
              <TrendingDown className="w-8 h-8 text-green-400" />
              <div>
                <div className="text-2xl font-bold text-green-400">{data.summary.prioritySummary.low}</div>
                <div className="text-sm text-green-300">Low Priority</div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['summary', 'nonCompliant', 'compliant', 'notApplicable', 'unanswered'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? 'bg-violet-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {tab === 'summary' && 'Summary'}
              {tab === 'nonCompliant' && `Deficiencies (${data.summary.nonCompliantCount})`}
              {tab === 'compliant' && `Compliant (${data.summary.compliantCount})`}
              {tab === 'notApplicable' && `N/A (${data.summary.notApplicableCount})`}
              {tab === 'unanswered' && `Unanswered (${data.summary.unansweredCount})`}
            </button>
          ))}
        </div>

        {/* Filter/Sort Controls for Deficiencies Tab */}
        {activeTab === 'nonCompliant' && data.summary.nonCompliantCount > 0 && (
          <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Filter by Priority:</span>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="priority-filter"
              >
                <option value="all">All Priorities</option>
                <option value="High">High Only</option>
                <option value="Medium">Medium Only</option>
                <option value="Low">Low Only</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="sort-by"
              >
                <option value="priority">Priority</option>
                <option value="category">Category</option>
                <option value="status">Status</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-600 text-sm"
                data-testid="sort-order-btn"
              >
                {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
              </button>
            </div>
            
            <div className="text-sm text-slate-500">
              Showing {filteredDeficiencies.length} of {data.summary.nonCompliantCount} deficiencies
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'summary' && (
          <div className="space-y-6">
            {/* Priority Alert */}
            {data.summary.nonCompliantCount > 0 && (
              <div className={`rounded-xl p-6 ${
                data.summary.prioritySummary?.high > 0 
                  ? 'bg-red-500/10 border border-red-500/30' 
                  : 'bg-yellow-500/10 border border-yellow-500/30'
              }`}>
                <div className="flex items-start gap-4">
                  {data.summary.prioritySummary?.high > 0 ? (
                    <Flame className="w-8 h-8 text-red-400 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-8 h-8 text-yellow-400 flex-shrink-0" />
                  )}
                  <div>
                    <h3 className={`text-lg font-semibold mb-2 ${
                      data.summary.prioritySummary?.high > 0 ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {data.summary.prioritySummary?.high > 0 
                        ? `${data.summary.prioritySummary.high} High Priority Issues Require Immediate Attention`
                        : 'Action Required'
                      }
                    </h3>
                    <p className="text-slate-300">
                      You have <strong>{data.summary.nonCompliantCount}</strong> non-compliant findings that require remediation.
                      Review the Deficiencies tab for detailed remediation steps and priority assignments.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Categories Overview */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Findings by Category</h3>
              <div className="space-y-3">
                {Object.entries(
                  [...data.findings.compliant, ...data.findings.nonCompliant].reduce((acc, f) => {
                    acc[f.category] = acc[f.category] || { compliant: 0, nonCompliant: 0 };
                    if (data.findings.compliant.find(c => c.questionId === f.questionId)) {
                      acc[f.category].compliant++;
                    } else {
                      acc[f.category].nonCompliant++;
                    }
                    return acc;
                  }, {})
                ).map(([category, counts]) => {
                  const total = counts.compliant + counts.nonCompliant;
                  const percentage = Math.round((counts.compliant / total) * 100);
                  const barColor = percentage >= 75 
                    ? 'from-green-500 to-green-400' 
                    : percentage > 50 
                    ? 'from-yellow-500 to-yellow-400' 
                    : 'from-red-500 to-red-400';
                  const textColor = percentage >= 75 
                    ? 'text-green-400' 
                    : percentage > 50 
                    ? 'text-yellow-400' 
                    : 'text-red-400';
                  return (
                    <div key={category}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-300 text-sm">{category}</span>
                        <span className={`text-sm font-medium ${textColor}`}>{percentage}% compliant</span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${barColor} rounded-full`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Deficiencies Preview */}
            {data.findings.nonCompliant.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">High Priority Deficiencies</h3>
                  <button
                    onClick={() => setActiveTab('nonCompliant')}
                    className="text-sm text-violet-400 hover:text-violet-300"
                  >
                    View all →
                  </button>
                </div>
                <div className="space-y-3">
                  {data.findings.nonCompliant
                    .filter(f => f.priority === 'High')
                    .slice(0, 3)
                    .map((finding) => renderFindingCard(finding, "nonCompliant"))}
                  {data.findings.nonCompliant.filter(f => f.priority === 'High').length === 0 && (
                    <p className="text-slate-400 text-sm">No high priority deficiencies</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'nonCompliant' && (
          <div className="space-y-3">
            {filteredDeficiencies.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
                <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  {priorityFilter !== 'all' ? 'No Matching Deficiencies' : 'No Deficiencies'}
                </h3>
                <p className="text-slate-400">
                  {priorityFilter !== 'all' 
                    ? `No ${priorityFilter.toLowerCase()} priority deficiencies found` 
                    : 'Great job! No non-compliant findings'}
                </p>
              </div>
            ) : (
              filteredDeficiencies.map((finding) => renderFindingCard(finding, 'nonCompliant'))
            )}
          </div>
        )}

        {activeTab !== 'summary' && activeTab !== 'nonCompliant' && (
          <div className="space-y-3">
            {data.findings[activeTab]?.length === 0 ? (
              <div className="bg-slate-800/50 rounded-xl p-12 border border-slate-700 text-center">
                <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Items</h3>
                <p className="text-slate-400">No findings in this category</p>
              </div>
            ) : (
              data.findings[activeTab]?.map((finding) => renderFindingCard(finding, activeTab))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Findings;
